import { Coordinate } from '@shared/models/geography.model';
import { getJulianCentury, getJulianDate } from './date.helpers';

// Utility functions
const deg2rad = (d: number) => (d * Math.PI) / 180;
const rad2deg = (r: number) => (r * 180) / Math.PI;
const normalizeAzimuth = (az: number) => ((az % 360) + 360) % 360;

/**
 * Calculate mean anomaly of the Sun (degrees)
 * Mean anomaly is the angle from periapsis (closest point to Sun) if Earth moved in a circular orbit at constant speed.
 * @param jCentury Julian centuries since J2000 epoch
 * @returns Mean anomaly in degrees (0-360)
 */
const calculateMeanAnomaly = (jCentury: number): number => {
  const M = 357.52911 + jCentury * (35999.05029 - 0.0001537 * jCentury);
  return normalizeAzimuth(M);
};

/**
 * Calculate mean longitude of the Sun (degrees)
 * Mean longitude is the Sun's position along the ecliptic if it moved at constant speed.
 * @param jCentury Julian centuries since J2000 epoch
 * @returns Mean longitude in degrees (0-360)
 */
const calculateMeanLongitude = (jCentury: number): number => {
  const L0 = 280.46646 + jCentury * (36000.76983 + 0.0003032 * jCentury);
  return normalizeAzimuth(L0);
};

/**
 * Calculate eccentricity of Earth's orbit
 * Eccentricity measures how elliptical Earth's orbit is (0 = perfect circle, 1 = parabola)
 * @param jCentury Julian centuries since J2000 epoch
 * @returns Eccentricity (dimensionless, ~0.0167)
 */
const calculateEccentricity = (jCentury: number): number => {
  return 0.016708634 - jCentury * (0.000042037 + 0.0000001267 * jCentury);
};

/**
 * Calculate equation of center (correction to mean anomaly)
 * Equation of center corrects for Earth's elliptical orbit - the difference between true and mean anomaly.
 * @param jCentury Julian centuries since J2000 epoch
 * @param meanAnomaly Mean anomaly in degrees
 * @returns Equation of center in degrees
 */
const calculateEquationOfCenter = (
  jCentury: number,
  meanAnomaly: number,
): number => {
  const Mrad = deg2rad(meanAnomaly);
  return (
    (1.914602 - jCentury * (0.004817 + 0.000014 * jCentury)) * Math.sin(Mrad) +
    (0.019993 - 0.000101 * jCentury) * Math.sin(2 * Mrad) +
    0.000289 * Math.sin(3 * Mrad)
  );
};

/**
 * Calculate true longitude of the Sun (degrees)
 * True longitude is the Sun's actual position along the ecliptic, corrected for orbital ellipticity.
 * @param meanLongitude Mean longitude in degrees
 * @param equationOfCenter Equation of center in degrees
 * @param jCentury Julian centuries since J2000 epoch
 * @returns True longitude in degrees (0-360)
 */
const calculateTrueSunLongitude = (
  meanLongitude: number,
  equationOfCenter: number,
  jCentury: number,
): number => {
  let lambda = meanLongitude + equationOfCenter;
  lambda -= 0.00569 + 0.00478 * Math.sin(deg2rad(125.04 - 1934.136 * jCentury));
  return normalizeAzimuth(lambda);
};

/**
 * Calculate obliquity of the ecliptic (degrees)
 * Obliquity is Earth's axial tilt - the angle between Earth's rotation axis and the perpendicular to its orbit.
 * @param jCentury Julian centuries since J2000 epoch
 * @returns Obliquity in degrees (~23.44)
 */
const calculateObliquity = (jCentury: number): number => {
  // Base obliquity in degrees-minutes-seconds format: 23° 26' 21.448"
  const degrees = 23;
  const minutes = 26;
  const seconds =
    21.448 - jCentury * (46.815 + jCentury * (0.00059 - 0.001813 * jCentury));

  const epsilon0 = degrees + minutes / 60 + seconds / 3600;

  // Nutation correction
  const nutationCorrection =
    0.00256 * Math.cos(deg2rad(125.04 - 1934.136 * jCentury));

  return epsilon0 + nutationCorrection;
};

/**
 * Calculate solar declination (degrees)
 * Solar declination is the angle between the Sun and the celestial equator, determining seasons.
 * @param trueLongitude True longitude of the Sun in degrees
 * @param obliquity Obliquity of the ecliptic in degrees
 * @returns Solar declination in degrees
 */
const calculateDeclination = (
  trueLongitude: number,
  obliquity: number,
): number => {
  const lambdaRad = deg2rad(trueLongitude);
  const epsilonRad = deg2rad(obliquity);
  const sinDec = Math.sin(epsilonRad) * Math.sin(lambdaRad);
  return rad2deg(Math.asin(sinDec));
};

/**
 * Calculate equation of time (minutes)
 * Equation of time is the difference between apparent solar time and mean solar time.
 * It accounts for Earth's elliptical orbit and axial tilt, causing sundials to be up to ±16 minutes off from clocks.
 * @param jCentury Julian centuries since J2000 epoch
 * @param meanAnomaly Mean anomaly in degrees
 * @param meanLongitude Mean longitude in degrees
 * @param obliquity Obliquity of the ecliptic in degrees
 * @returns Equation of time in minutes
 */
const calculateEquationOfTime = (
  jCentury: number,
  meanAnomaly: number,
  meanLongitude: number,
  obliquity: number,
): number => {
  const y = Math.tan(deg2rad(obliquity) / 2) ** 2;
  const L0rad = deg2rad(meanLongitude);
  const Mrad = deg2rad(meanAnomaly);
  const e = calculateEccentricity(jCentury);

  return (
    4 *
    rad2deg(
      y * Math.sin(2 * L0rad) -
        2 * e * Math.sin(Mrad) +
        4 * e * y * Math.sin(Mrad) * Math.cos(2 * L0rad) -
        0.5 * y * y * Math.sin(4 * L0rad) -
        1.25 * e * e * Math.sin(2 * Mrad),
    )
  );
};

/**
 * Calculate true solar time (minutes)
 * True solar time is when the Sun would appear on a sundial, adjusted for longitude and orbital variations.
 * @param utcMinutes UTC minutes since midnight
 * @param equationOfTime Equation of time in minutes
 * @param longitude Longitude in degrees
 * @returns True solar time in minutes (0-1440)
 */
const calculateTrueSolarTime = (
  utcMinutes: number,
  equationOfTime: number,
  longitude: number,
): number => {
  return (utcMinutes + equationOfTime + 4 * longitude + 1440) % 1440;
};

/**
 * Calculate hour angle (radians)
 * Hour angle is the angular distance of the Sun from local solar noon (0° = noon, ±180° = midnight).
 * @param trueSolarTime True solar time in minutes
 * @returns Hour angle in radians
 */
const calculateHourAngle = (trueSolarTime: number): number => {
  return deg2rad(trueSolarTime / 4 - 180);
};

/**
 * Calculate solar elevation angle (radians)
 * Solar elevation is the angle of the Sun above the horizon (0° = horizon, 90° = zenith).
 * @param latitude Latitude in degrees
 * @param declination Solar declination in degrees
 * @param hourAngle Hour angle in radians
 * @returns Solar elevation angle in radians
 */
const calculateSolarElevation = (
  latitude: number,
  declination: number,
  hourAngle: number,
): number => {
  const latRad = deg2rad(latitude);
  const decRad = deg2rad(declination);
  const cosZenith =
    Math.sin(latRad) * Math.sin(decRad) +
    Math.cos(latRad) * Math.cos(decRad) * Math.cos(hourAngle);
  const zenith = Math.acos(Math.min(Math.max(cosZenith, -1), 1));
  return Math.PI / 2 - zenith;
};

/**
 * Calculate solar azimuth angle (degrees)
 * Solar azimuth is the compass direction of the Sun (0°=North, 90°=East, 180°=South, 270°=West).
 * @param latitude Latitude in degrees
 * @param declination Solar declination in degrees
 * @param hourAngle Hour angle in radians
 * @returns Solar azimuth angle in degrees (0=N, 90=E)
 */
const calculateSolarAzimuth = (
  latitude: number,
  declination: number,
  hourAngle: number,
): number => {
  const latRad = deg2rad(latitude);
  const decRad = deg2rad(declination);
  const sinH = Math.sin(hourAngle);
  const cosH = Math.cos(hourAngle);

  // SPA-formula (azimuth from S, W positive)
  const azSpa = Math.atan2(
    sinH,
    cosH * Math.sin(latRad) - Math.tan(decRad) * Math.cos(latRad),
  );

  // Convert to 0°=N, clockwise
  return normalizeAzimuth(rad2deg(azSpa) + 180);
};

/**
 * Calculate complete solar position for a given date and location
 * @param date Date to calculate solar position for
 * @param coords Geographic coordinates
 * @returns Solar elevation (radians) and azimuth (degrees)
 */
export const calculateSolarPosition = (
  date: Date,
  coords: Coordinate,
): { elevationRad: number; azimuthDeg: number } => {
  const julianDate = getJulianDate(date);
  const julianCentury = getJulianCentury(julianDate);

  const meanAnomaly = calculateMeanAnomaly(julianCentury);
  const meanLongitude = calculateMeanLongitude(julianCentury);
  const equationOfCenter = calculateEquationOfCenter(
    julianCentury,
    meanAnomaly,
  );
  const trueLongitude = calculateTrueSunLongitude(
    meanLongitude,
    equationOfCenter,
    julianCentury,
  );
  const obliquity = calculateObliquity(julianCentury);
  const declination = calculateDeclination(trueLongitude, obliquity);
  const equationOfTime = calculateEquationOfTime(
    julianCentury,
    meanAnomaly,
    meanLongitude,
    obliquity,
  );

  const utcMinutes = date.getUTCHours() * 60;
  const trueSolarTime = calculateTrueSolarTime(
    utcMinutes,
    equationOfTime,
    coords.lng,
  );
  const hourAngle = calculateHourAngle(trueSolarTime);

  const elevationRad = calculateSolarElevation(
    coords.lat,
    declination,
    hourAngle,
  );
  const azimuthDeg = calculateSolarAzimuth(coords.lat, declination, hourAngle);

  return { elevationRad, azimuthDeg };
};
